import React, { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Save, Plus, Trash2, Edit2, X, Check, Circle, Square, Theater } from 'lucide-react';
import { getSeatingPlanById, updateSeatingPlan, seatingPlan } from '../services/eventService';
import { v4 as uuidv4 } from 'uuid';

const RoundTableEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const stageRef = useRef(null);
  const [isLoading, setIsLoading] = useState(true);
  const [seatMapName, setSeatMapName] = useState('Untitled Round Table Layout');
  const [editingSeatMapName, setEditingSeatMapName] = useState(false);
  const [seatMapNameEditValue, setSeatMapNameEditValue] = useState('');
  const seatMapNameInputRef = useRef(null);
  const seatNumber = null;
  // Add table modal state
  const [showAddTableModal, setShowAddTableModal] = useState(false);
  const [pendingTableType, setPendingTableType] = useState('round');
  const [pendingSeatCount, setPendingSeatCount] = useState(8);
  const [pendingMaxSeats, setPendingMaxSeats] = useState(12);

  // Table state
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [editingTable, setEditingTable] = useState(null);
  const [editingSeat, setEditingSeat] = useState(null);
  const [seatNameInput, setSeatNameInput] = useState('');
  const [editingSeatColor, setEditingSeatColor] = useState('');
  const [draggingTable, setDraggingTable] = useState(null);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Stage state
  const [hasStage, setHasStage] = useState(false);
  const [stageName, setStageName] = useState('STAGE');
  const [editingStage, setEditingStage] = useState(false);
  const [stageEditValue, setStageEditValue] = useState('');
  const [stagePosition, setStagePosition] = useState({ x: 0, y: 50 }); // Will be set to center on mount
  const [draggingStage, setDraggingStage] = useState(false);
  const [stageDragOffset, setStageDragOffset] = useState({ x: 0, y: 0 });
  const stageInputRef = useRef(null);

  // Canvas state
  const [stageSize, setStageSize] = useState({ width: 1200, height: 11000 });
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  
  // Layout tools state
  const [snapToGrid, setSnapToGrid] = useState(true);
  const [gridSize, setGridSize] = useState(50);
  const [showGrid, setShowGrid] = useState(true);

  // Table dimensions
  const tableRadius = 40;
  const rectangularTableWidth = 120;
  const rectangularTableHeight = 80;
  const seatRadius = 12;
  const seatDistance = 35; // Distance from table edge

  useEffect(() => {
    if (editingSeatMapName && seatMapNameInputRef.current) {
      seatMapNameInputRef.current.focus();
    }
  }, [editingSeatMapName]);

  useEffect(() => {
    if (editingStage && stageInputRef.current) {
      stageInputRef.current.focus();
    }
  }, [editingStage]);

  // Initialize stage position to center if not loaded from API
  useEffect(() => {
    if (stagePosition.x === 0 && !id && hasStage) {
      setStagePosition({ x: stageSize.width / 2, y: 50 });
    }
  }, [stageSize.width, id, hasStage]);

  useEffect(() => {
    const loadSeatingPlan = async () => {
      try {
        setIsLoading(true);
        if (id) {
          const response = await getSeatingPlanById(id);
          const seatmapData = response?.data || response;
          
          if (!seatmapData || !Array.isArray(seatmapData) || seatmapData.length === 0) {
            throw new Error('Invalid API response');
          }

          const seatmap = seatmapData[0];
          
          if (seatmap.name) {
            setSeatMapName(seatmap.name);
          }

          // Load stage if available
          if (seatmap.stageName || seatmap.stagePosition) {
            setHasStage(true);
            if (seatmap.stageName) {
              setStageName(seatmap.stageName);
            }
            // Load stage position if available
            if (seatmap.stagePosition) {
              setStagePosition(seatmap.stagePosition);
            } else if (seatmap.stageX !== undefined && seatmap.stageY !== undefined) {
              // Legacy support for old format
              setStagePosition({ x: seatmap.stageX, y: seatmap.stageY });
            }
          }

          // Load layout settings if available
          if (seatmap.layoutSettings) {
            setSnapToGrid(seatmap.layoutSettings.snapToGrid ?? true);
            setGridSize(seatmap.layoutSettings.gridSize ?? 50);
            setShowGrid(seatmap.layoutSettings.showGrid ?? false);
          }

          // Note: maxSeatsPerTable is now per-table, loaded with each table

          // Transform API data to table format
          if (seatmap.sections && Array.isArray(seatmap.sections)) {
            const loadedTables = [];
            
            seatmap.sections.forEach((section, sectionIndex) => {
              const firstSeat =
  section.rows?.[0]?.seats?.find(s => s.type === 'seat') ||
  section.rows?.[0]?.seats?.[0];

const adultPrice =
  firstSeat?.price ?? 0;

const childPrice =
  firstSeat?.childprice ?? 0;
              // Each section represents a table
              const tableData = {
                id: section.id || uuidv4(),
                name: section.name || `Table ${sectionIndex + 1}`,
                type: section.tableType || 'round',
                x: section.x || 100 + (sectionIndex % 4) * 200,
                y: section.y || 100 + Math.floor(sectionIndex / 4) * 200,
                category: section.category || 'standard',
                // adultPrice: section.adultPrice || section.price || 0, // Support legacy price field
                // childPrice: section.childPrice || 0,
//                 adultPrice: section.adultPrice ?? section.price ?? 0,
// childPrice: section.childPrice ?? 0,
 adultPrice,
  childPrice,
                color: section.color || '#8B4513',
                maxSeatsPerTable: section.maxSeatsPerTable || 12,
                seats: []
              };

              // Extract seats from rows
              if (section.rows && Array.isArray(section.rows)) {
                section.rows.forEach(row => {
                  if (row.seats && Array.isArray(row.seats)) {
                    row.seats.forEach((seat, seatIndex) => {
                      if (seat.type === 'seat') {
                        tableData.seats.push({
                          id: seat.id || seat._id || uuidv4(),
                          name: seat.name || seat.label || '',
                          reserved: seat.reserved || false,
                          angle: seat.angle || (seatIndex / (row.seats.length || 1)) * 2 * Math.PI,
                          position: seat.position || seatIndex,
                          color: seat.color || null
                        });
                      }
                    });
                  }
                });
              }

              loadedTables.push(tableData);
            });

            setTables(loadedTables);
          }
        } 
        else {
          // No ID provided - create new layout with two default round tables
          const defaultTables = [];
          const spacing = 250;
          const startX = 300;
          const startY = 400;
          
          for (let i = 0; i < 2; i++) {
            const tableId = uuidv4();
            const seats = Array.from({ length: 8 }).map((_, seatIndex) => {
              const angle = (seatIndex / 8) * 2 * Math.PI;
              return {
                id: uuidv4(),
                name: '',
                reserved: false,
                angle,
                position: seatIndex,
                color: null
              };
            });
            
            defaultTables.push({
              id: tableId,
              name: `Table ${i + 1}`,
              type: 'round',
              x: startX + (i * spacing),
              y: startY,
              category: 'standard',
              adultPrice: 0,
              childPrice: 0,
              color: '#8B4513',
              maxSeatsPerTable: 12,
              seats
            });
          }
          
          setTables(defaultTables);
        }
      } catch (error) {
        console.error('Error loading seating plan:', error);
        toast.error(`Failed to load seating plan: ${error.message}`);
      } finally {
        setIsLoading(false);
      }
    };

    loadSeatingPlan();
  }, [id]);
// Extract prices from first seat (authoritative source)


  const handleAddTableClick = (type) => {
    setPendingTableType(type);
    setPendingSeatCount(8);
    // Max 12 seats for Round, Max 10 seats for Rectangular
    setPendingMaxSeats(type === 'round' ? 12 : 10);
    setShowAddTableModal(true);
  };

  const handleAddTableConfirm = () => {
    if (pendingSeatCount < 1 || pendingSeatCount > pendingMaxSeats) {
      toast.error(`Seat count must be between 1 and ${pendingMaxSeats}`);
      return;
    }
    
    if (pendingMaxSeats < 1 || pendingMaxSeats > 50) {
      toast.error('Maximum seats must be between 1 and 50');
      return;
    }

    const tableId = uuidv4();
    const tableNumber = tables.length + 1;
    
    // Calculate position (grid layout)
    const cols = Math.floor(Math.sqrt(tables.length + 1));
    const x = 150 + (tables.length % cols) * 250;
    const y = 150 + Math.floor(tables.length / cols) * 250;

    const seats = Array.from({ length: pendingSeatCount }).map((_, i) => {
      const angle = (i / pendingSeatCount) * 2 * Math.PI;
      return {
        id: uuidv4(),
        name: '',
        reserved: false,
        angle,
        position: i,
        color: null // Custom seat color
      };
    });

    const newTable = {
      id: tableId,
      name: `Table ${tableNumber}`,
      type: pendingTableType,
      x,
      y,
      category: 'standard',
      adultPrice: 0,
      childPrice: 0,
      color: '#8B4513', // Default table color
      maxSeatsPerTable: pendingMaxSeats,
      seats
    };

    setTables([...tables, newTable]);
    setShowAddTableModal(false);
    toast.success(`${pendingTableType === 'round' ? 'Round' : 'Rectangular'} table added with ${pendingSeatCount} seats`);
  };

  const removeTable = (tableId) => {
    setTables(tables.filter(t => t.id !== tableId));
    if (selectedTable === tableId) {
      setSelectedTable(null);
    }
    toast.success('Table removed');
  };

  const handleTableMouseDown = (tableId, event) => {
    event.stopPropagation();
    const table = tables.find(t => t.id === tableId);
    if (!table) return;
    
    const svg = stageRef.current;
    if (!svg) return;
    
    const rect = svg.getBoundingClientRect();
    const x = (event.clientX - rect.left) * (stageSize.width / rect.width);
    const y = (event.clientY - rect.top) * (stageSize.height / rect.height);
    
    setDraggingTable(tableId);
    setDragOffset({
      x: x - table.x,
      y: y - table.y
    });
    // Don't set selectedTable on mouse down - only on right-click
  };

  const handleTableRightClick = (tableId, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedTable(tableId);
    setDraggingTable(null); // Cancel any dragging
  };

  const handleTableMouseMove = (event) => {
    if (!stageRef.current) return;
    
    const svg = stageRef.current;
    const rect = svg.getBoundingClientRect();
    const x = (event.clientX - rect.left) * (stageSize.width / rect.width);
    const y = (event.clientY - rect.top) * (stageSize.height / rect.height);
    
    // Handle stage dragging
    if (draggingStage) {
      const stageWidth = 320;
      const stageHeight = 60;
      let newX = Math.max(stageWidth / 2, Math.min(stageSize.width - stageWidth / 2, x - stageDragOffset.x));
      let newY = Math.max(stageHeight / 2, Math.min(stageSize.height - stageHeight / 2, y - stageDragOffset.y));
      
      // Apply snap to grid if enabled
      if (snapToGrid) {
        newX = Math.round(newX / gridSize) * gridSize;
        newY = Math.round(newY / gridSize) * gridSize;
        newX = Math.max(stageWidth / 2, Math.min(stageSize.width - stageWidth / 2, newX));
        newY = Math.max(stageHeight / 2, Math.min(stageSize.height - stageHeight / 2, newY));
      }
      
      setStagePosition({ x: newX, y: newY });
      return;
    }
    
    // Handle table dragging
    if (!draggingTable) return;
    
    let newX = Math.max(50, Math.min(stageSize.width - 50, x - dragOffset.x));
    let newY = Math.max(50, Math.min(stageSize.height - 50, y - dragOffset.y));
    
    // Apply snap to grid if enabled - snap the final table position
    if (snapToGrid) {
      newX = Math.round(newX / gridSize) * gridSize;
      newY = Math.round(newY / gridSize) * gridSize;
      // Ensure snapped position is still within bounds
      newX = Math.max(50, Math.min(stageSize.width - 50, newX));
      newY = Math.max(50, Math.min(stageSize.height - 50, newY));
    }
    
    setTables(tables.map(table => 
      table.id === draggingTable ? { ...table, x: newX, y: newY } : table
    ));
  };

  const handleTableMouseUp = () => {
    setDraggingTable(null);
    setDragOffset({ x: 0, y: 0 });
    setDraggingStage(false);
    setStageDragOffset({ x: 0, y: 0 });
  };

  const handleStageMouseDown = (event) => {
    event.stopPropagation();
    const svg = stageRef.current;
    if (!svg) return;
    
    const rect = svg.getBoundingClientRect();
    const x = (event.clientX - rect.left) * (stageSize.width / rect.width);
    const y = (event.clientY - rect.top) * (stageSize.height / rect.height);
    
    setDraggingStage(true);
    setStageDragOffset({
      x: x - stagePosition.x,
      y: y - stagePosition.y
    });
  };

  const handleSeatClick = (tableId, seatId, event) => {
    event.stopPropagation();
    event.preventDefault();
    setDraggingTable(null); // Prevent table drag when clicking seat
    setSelectedTable(tableId);
    setEditingSeat({ tableId, seatId });
    const table = tables.find(t => t.id === tableId);
    const seat = table?.seats.find(s => s.id === seatId);
    setSeatNameInput(seat?.name || '');
    setEditingSeatColor(seat?.color || '');
  };

  const handleSeatNameUpdate = () => {
    if (!editingSeat) return;
    
    const table = tables.find(t => t.id === editingSeat.tableId);
    const seat = table?.seats.find(s => s.id === editingSeat.seatId);
    const currentReserved = seat?.reserved || false;
    
    setTables(tables.map(table => {
      if (table.id !== editingSeat.tableId) return table;
      return {
        ...table,
        seats: table.seats.map(seat => 
          seat.id === editingSeat.seatId 
            ? { 
                ...seat, 
                name: seatNameInput.trim(), 
                reserved: currentReserved, // Keep existing reserved status
                color: editingSeatColor || seat.color || null
              }
            : seat
        )
      };
    }));

    setEditingSeat(null);
    setSeatNameInput('');
    setEditingSeatColor('');
    toast.success('Seat updated');
  };

  const handleReservedChange = (checked) => {
    if (!editingSeat) return;
    
    setTables(tables.map(table => {
      if (table.id !== editingSeat.tableId) return table;
      return {
        ...table,
        seats: table.seats.map(seat => 
          seat.id === editingSeat.seatId 
            ? { ...seat, reserved: checked }
            : seat
        )
      };
    }));
    toast.success(`Seat ${checked ? 'reserved' : 'unreserved'}`);
  };

  const handleRemoveSeat = () => {
    if (!editingSeat) return;
    
    const table = tables.find(t => t.id === editingSeat.tableId);
    if (!table) return;
    
    console.log('=== REMOVING SEAT ===');
    console.log('Original seats:', table.seats.map(s => ({ id: s.id, position: s.position, name: s.name })));
    console.log('Removing seat ID:', editingSeat.seatId);
    
    // Filter out the removed seat, then sort by position to maintain order
    const remainingSeats = table.seats
      .filter(seat => seat.id !== editingSeat.seatId)
         .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
      // .sort((a, b) => (a.position || 0) - (b.position || 0));
    const newSeatCount = remainingSeats.length;
    
    console.log('Remaining seats after filter & sort:', remainingSeats.map(s => ({ id: s.id, position: s.position, name: s.name })));
    
    const updatedSeats = remainingSeats.map((seat, index) => ({
      ...seat,
      // angle: newSeatCount > 0 ? (index / newSeatCount) * 2 * Math.PI : 0,
      position: index,
        angle: newSeatCount
      ? (index / newSeatCount) * 2 * Math.PI
      : 0,
    name: ` ${index + 1}` 
    }));
    
    console.log('Updated seats with new positions:', updatedSeats.map(s => ({ id: s.id, position: s.position, name: s.name })));
    
    // setTables(tables.map(t => {
    //   if (t.id !== editingSeat.tableId) return t;
    //   return {
    //     ...t,
    //     seats: updatedSeats
    //   };
    // }));
  setTables(prev =>
    prev.map(t =>
      t.id === editingSeat.tableId
        ? { ...t, seats: updatedSeats }
        : t
    )
  );
    setEditingSeat(null);
    setSeatNameInput('');
    setEditingSeatColor('');
    toast.success('Seat removed');
  };

  const addSeatToTable = (tableId) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) return;
    
    const maxSeats = table.maxSeatsPerTable || 12;
    if (table.seats.length >= maxSeats) {
      toast.error(`Maximum ${maxSeats} seats per table`);
      return;
    }

    // Add new seat and recalculate all seat angles to be evenly spaced
    const newSeatCount = table.seats.length + 1;
    const newSeat = {
      id: uuidv4(),
      name: '',
      reserved: false,
      angle: 0, // Will be recalculated
      position: table.seats.length,
      color: null
    };

    // Recalculate all seat angles to be evenly distributed
    const updatedSeats = [...table.seats, newSeat].map((seat, index) => ({
      ...seat,
      angle: (index / newSeatCount) * 2 * Math.PI,
      position: index
    }));
    
    setTables(tables.map(t => 
      t.id === tableId 
        ? {
            ...t,
            seats: updatedSeats
          }
        : t
    ));
    toast.success('Seat added');
  };

  const removeSeatFromTable = (tableId, seatId) => {
    const table = tables.find(t => t.id === tableId);
    if (!table) return;
    
    // Filter out the removed seat, then sort by position to maintain order
    const remainingSeats = table.seats
      .filter(seat => seat.id !== seatId)
      .sort((a, b) => (a.position || 0) - (b.position || 0));
    const newSeatCount = remainingSeats.length;
    
    setTables(tables.map(t => {
      if (t.id !== tableId) return t;
      return {
        ...t,
        seats: remainingSeats.map((seat, index) => ({
          ...seat,
          angle: newSeatCount > 0 ? (index / newSeatCount) * 2 * Math.PI : 0,
          position: index
        }))
      };
    }));
    toast.success('Seat removed');
  };

  const updateTableName = (tableId, newName) => {
    setTables(tables.map(table => 
      table.id === tableId ? { ...table, name: newName } : table
    ));
  };

  const updateTableProperty = (tableId, property, value) => {
    setTables(tables.map(table => 
      table.id === tableId ? { ...table, [property]: value } : table
    ));
  };

  const removeStage = () => {
    if (window.confirm('Delete stage?')) {
      setHasStage(false);
      setStagePosition({ x: 0, y: 50 });
      toast.success('Stage removed');
    }
  };

  const handleStageUpdate = () => {
    if (stageEditValue.trim()) {
      setStageName(stageEditValue.trim());
      toast.success('Stage name updated');
    } else {
      toast.error('Stage name cannot be empty');
    }
    setEditingStage(false);
  };

  const handleStageKeyDown = (e) => {
    if (e.key === 'Enter') handleStageUpdate();
    else if (e.key === 'Escape') setEditingStage(false);
  };

  const saveSeatMapToApi = async () => {
    const transformed = {
      name: seatMapName,
      type: 'round',
      stageName: hasStage ? stageName : null,
      stagePosition: hasStage ? stagePosition : null,
      config: {},
      layoutSettings: {
        snapToGrid,
        gridSize,
        showGrid
      },
      sections: tables.map(table => {
        
        // Calculate seat positions based on table type
        const seats = table.seats.map((seat, index) => {
          let x, y;
          if (table.type === 'round') {
            x = table.x + Math.cos(seat.angle) * (tableRadius + seatDistance);
            y = table.y + Math.sin(seat.angle) * (tableRadius + seatDistance);
          } else {
            // Rectangular table - fill top and bottom first (3 each), then left and right
            const totalSeats = table.seats.length;
            
            // Priority: Top 3, Bottom 3, then distribute remaining to left and right
            const topSeats = Math.min(3, totalSeats);
            const remainingAfterTop = Math.max(0, totalSeats - topSeats);
            const bottomSeats = Math.min(3, remainingAfterTop);
            const remainingAfterBottom = Math.max(0, remainingAfterTop - bottomSeats);
            
            // Distribute remaining seats equally between left and right
            const leftSeats = Math.ceil(remainingAfterBottom / 2);
            const rightSeats = Math.floor(remainingAfterBottom / 2);
            
            const halfWidth = rectangularTableWidth / 2;
            const halfHeight = rectangularTableHeight / 2;
            
            if (index < topSeats) {
              // Top edge - distribute evenly from left to right
              const spacing = topSeats > 1 ? rectangularTableWidth / (topSeats - 1) : 0;
              x = table.x - halfWidth + (index * spacing);
              y = table.y - halfHeight - seatDistance;
            } else if (index < topSeats + bottomSeats) {
              // Bottom edge - distribute evenly from right to left
              const edgeIndex = index - topSeats;
              const spacing = bottomSeats > 1 ? rectangularTableWidth / (bottomSeats - 1) : 0;
              x = table.x + halfWidth - (edgeIndex * spacing);
              y = table.y + halfHeight + seatDistance;
            } else if (index < topSeats + bottomSeats + rightSeats) {
              // Right edge - center seats and bring them closer together
              const edgeIndex = index - topSeats - bottomSeats;
              // Use smaller spacing for left/right edges to bring seats closer
              const maxSpacing = Math.min(rectangularTableHeight * 0.6, 50); // Limit max spacing
              const spacing = rightSeats > 1 ? maxSpacing / (rightSeats - 1) : 0;
              const offset = (rightSeats - 1) * spacing / 2; // Center the group
              x = table.x + halfWidth + seatDistance;
              y = table.y - offset + (edgeIndex * spacing);
            } else {
              // Left edge - center seats and bring them closer together
              const edgeIndex = index - topSeats - bottomSeats - rightSeats;
              // Use smaller spacing for left/right edges to bring seats closer
              const maxSpacing = Math.min(rectangularTableHeight * 0.6, 50); // Limit max spacing
              const spacing = leftSeats > 1 ? maxSpacing / (leftSeats - 1) : 0;
              const offset = (leftSeats - 1) * spacing / 2; // Center the group
              x = table.x - halfWidth - seatDistance;
              y = table.y + offset - (edgeIndex * spacing);
            }
          }

          // Extract seat number from label or use index + 1
          // let seatNumber = null;
          
          if (seat.type === 'seat') {
            const labelNumber = parseInt((seat.name || `${index + 1}`).replace(/\D/g, ''), 10);
            if (!isNaN(labelNumber)) {
              seatNumber = labelNumber;
            } else {
              seatNumber = index + 1;
            }
          }


          // Get category color for fallback
          const categoryColor = '#96CEB4'; // Default color

          // Match SeatAdminPanel.js payload structure
          return {
            // seatNumber: seat.seatNumber,
            seatNumber: seat.seatNumber ?? index + 1,

            id: seat.id || null,
            label: seat.name || `${index + 1}`,
            type: 'seat',
            level: table.category || '',
            price: table.adultPrice || 0, // API uses 'price' for adult price (15+ years)
            childprice: table.childPrice || 0, // Child price (6-15 years) - note: lowercase 'childprice'
            reserved: seat.reserved || false,
            color: seat.color || categoryColor, // Use string, not null
            // Round-table specific fields (preserved for rendering)
            angle: seat.angle,
            position: seat.position,
            x,
            y
          };
        });

        return {
          
          name: table.name,
          // Round-table specific fields at section level
          seatNumber: table.seatNumber,
          id: table.id,
          tableType: table.type,
          category: table.category,
          adultPrice: table.adultPrice || 0,
          childPrice: table.childPrice || 0,
          price: table.adultPrice || 0, // Keep for backward compatibility
          color: table.color || '#8B4513',
          maxSeatsPerTable: table.maxSeatsPerTable || 12,
          x: table.x,
          y: table.y,
          rows: [{
            rowNumber: 1,
            seats
          }]
        };
      })
    };

    console.log(transformed,'transformed final round table layout data');

    try {
      let res;
      if (id) {
        res = await updateSeatingPlan(id, transformed);
      } else {
        res = await seatingPlan(null, transformed);
      }

      if (res.status === 200 || res.status === 201) {
        toast.success('Round table layout saved successfully!');
        if (!id && res.data?._id) {
          navigate(`/round-table-editor/${res.data._id}`);
        }
      } else {
        toast.error('Failed to save layout');
      }
    } catch (err) {
      console.error('Save error:', err);
      toast.error('An error occurred while saving');
    }
  };

  const getSeatPosition = (table, seat) => {
    if (table.type === 'round') {
      return {
        x: Math.cos(seat.angle) * (tableRadius + seatDistance),
        y: Math.sin(seat.angle) * (tableRadius + seatDistance)
      };
    } else {
      // Rectangular table - fill top and bottom first (3 each), then left and right
      const totalSeats = table.seats.length;
      
      // Priority: Top 3, Bottom 3, then distribute remaining to left and right
      const topSeats = Math.min(3, totalSeats);
      const remainingAfterTop = Math.max(0, totalSeats - topSeats);
      const bottomSeats = Math.min(3, remainingAfterTop);
      const remainingAfterBottom = Math.max(0, remainingAfterTop - bottomSeats);
      
      // Distribute remaining seats equally between left and right
      const leftSeats = Math.ceil(remainingAfterBottom / 2);
      const rightSeats = Math.floor(remainingAfterBottom / 2);
      
      const halfWidth = rectangularTableWidth / 2;
      const halfHeight = rectangularTableHeight / 2;
      const seatIndex = seat.position;
      
      let x, y;
      
      if (seatIndex < topSeats) {
        // Top edge - distribute evenly from left to right
        const spacing = topSeats > 1 ? rectangularTableWidth / (topSeats - 1) : 0;
        x = -halfWidth + (seatIndex * spacing);
        y = -halfHeight - seatDistance;
      } else if (seatIndex < topSeats + bottomSeats) {
        // Bottom edge - distribute evenly from right to left
        const edgeIndex = seatIndex - topSeats;
        const spacing = bottomSeats > 1 ? rectangularTableWidth / (bottomSeats - 1) : 0;
        x = halfWidth - (edgeIndex * spacing);
        y = halfHeight + seatDistance;
      } else if (seatIndex < topSeats + bottomSeats + rightSeats) {
        // Right edge - center seats and bring them closer together
        const edgeIndex = seatIndex - topSeats - bottomSeats;
        // Use smaller spacing for left/right edges to bring seats closer
        const maxSpacing = Math.min(rectangularTableHeight * 0.6, 50); // Limit max spacing
        const spacing = rightSeats > 1 ? maxSpacing / (rightSeats - 1) : 0;
        const offset = (rightSeats - 1) * spacing / 2; // Center the group
        x = halfWidth + seatDistance;
        y = -offset + (edgeIndex * spacing);
      } else {
        // Left edge - center seats and bring them closer together
        const edgeIndex = seatIndex - topSeats - bottomSeats - rightSeats;
        // Use smaller spacing for left/right edges to bring seats closer
        const maxSpacing = Math.min(rectangularTableHeight * 0.6, 50); // Limit max spacing
        const spacing = leftSeats > 1 ? maxSpacing / (leftSeats - 1) : 0;
        const offset = (leftSeats - 1) * spacing / 2; // Center the group
        x = -halfWidth - seatDistance;
        y = offset - (edgeIndex * spacing);
      }
      return { x, y };
    }
  };

  const renderTable = (table) => {
    const isSelected = selectedTable === table.id;
    const isDragging = draggingTable === table.id;
    // Sort seats by position to ensure correct label numbering
    const sortedSeats = [...table.seats].sort((a, b) => (a.position || 0) - (b.position || 0));
    const seatPositions = sortedSeats.map(seat => getSeatPosition(table, seat));
    
    // Debug log for rendering
    if (table.seats.length > 0) {
      console.log('=== RENDERING TABLE ===', table.name);
      console.log('Original seats array:', table.seats.map(s => ({ id: s.id, position: s.position, name: s.name })));
      console.log('Sorted seats array:', sortedSeats.map(s => ({ id: s.id, position: s.position, name: s.name })));
    }

    return (
      <g
        key={table.id}
        transform={`translate(${table.x}, ${table.y})`}
        className={isSelected ? 'table-selected' : ''}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        onMouseDown={(e) => handleTableMouseDown(table.id, e)}
        onContextMenu={(e) => handleTableRightClick(table.id, e)}
      >
        {/* Table shape */}
        {table.type === 'round' ? (
          <circle
            r={tableRadius}
            fill={table.color || "#8B4513"}
            stroke={isSelected ? "#FF6B6B" : "#000"}
            strokeWidth={isSelected ? 3 : 1}
            onContextMenu={(e) => handleTableRightClick(table.id, e)}
          />
        ) : (
          <rect
            x={-rectangularTableWidth / 2}
            y={-rectangularTableHeight / 2}
            width={rectangularTableWidth}
            height={rectangularTableHeight}
            fill={table.color || "#8B4513"}
            stroke={isSelected ? "#FF6B6B" : "#000"}
            strokeWidth={isSelected ? 3 : 1}
            rx={5}
            onContextMenu={(e) => handleTableRightClick(table.id, e)}
          />
        )}

        {/* Table name - inside the table */}
        <text
          x={0}
          y={table.type === 'round' ? 5 : 5}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize={table.type === 'round' ? "12" : "11"}
          fontWeight="bold"
          fill="#fff"
          onClick={(e) => {
            e.stopPropagation();
            setEditingTable(table.id);
          }}
          style={{ cursor: 'pointer', textShadow: '1px 1px 2px rgba(0,0,0,0.5)' }}
        >
          {table.name}
        </text>

        {/* Delete button */}
        <circle
          r={10}
          fill="#dc3545"
          cx={table.type === 'round' ? tableRadius + 30 : rectangularTableWidth / 2 + 5}
          cy={table.type === 'round' ? -tableRadius - 30 : -rectangularTableHeight / 2 - 5}
          onClick={(e) => {
            e.stopPropagation();
            if (window.confirm(`Delete ${table.name}?`)) {
              removeTable(table.id);
            }
          }}
          style={{ cursor: 'pointer' }}
        />
        <text
          x={table.type === 'round' ? tableRadius + 30 : rectangularTableWidth / 2 + 5}
          y={table.type === 'round' ? -tableRadius - 30 : -rectangularTableHeight / 2 - 5}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="12"
          fill="white"
          fontWeight="bold"
          pointerEvents="none"
        >
          ×
        </text>

        {/* Seats */}
        {sortedSeats.map((seat, index) => {
          const pos = seatPositions[index];
          const isEditing = editingSeat?.tableId === table.id && editingSeat?.seatId === seat.id;
          const displayLabel = seat.name || ((seat.position ?? index) + 1);
          
          // Debug log for each seat being rendered
          if (!seat.name) {
            
            console.log(`Rendering seat - ID: ${seat.id}, position: ${seat.position}, index: ${index}, displayLabel: ${displayLabel}`);
          }
          
          return (
            <g key={seat.id}>
              <circle
                cx={pos.x}
                cy={pos.y}
                r={seatRadius}
                fill={seat.color || "#96CEB4"}
                stroke={seat.reserved ? "#dc3545" : "#6BA87E"}
                strokeWidth={seat.reserved ? 3 : 1}
                opacity={seat.reserved ? 0.7 : 1}
                onClick={(e) => handleSeatClick(table.id, seat.id, e)}
                onMouseDown={(e) => e.stopPropagation()}
                style={{ cursor: 'pointer' }}
              />
              {seat.reserved && (
                <circle
                  cx={pos.x + seatRadius - 2}
                  cy={pos.y - seatRadius + 2}
                  r={6}
                  fill="#dc3545"
                  stroke="white"
                  strokeWidth="1"
                  pointerEvents="none"
                />
              )}
              {seat.reserved && (
                <text
                  x={pos.x + seatRadius - 2}
                  y={pos.y - seatRadius + 2}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="8"
                  fill="white"
                  fontWeight="bold"
                  pointerEvents="none"
                >
                  R
                </text>
              )}
              {seat.name ? (
                <text
                  x={pos.x}
                  y={pos.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="9"
                  fill="white"
                  fontWeight="bold"
                  pointerEvents="none"
                >
                  {seat.name.length > 8 ? seat.name.substring(0, 7) + '...' : seat.name}
                </text>
              ) : (
                <text
                  x={pos.x}
                  y={pos.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="10"
                  fill="white"
                  fontWeight="bold"
                  pointerEvents="none"
                >
                  {displayLabel}
                </text>
              )}
            </g>
          );
        })}
      </g>
    );
  };

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-4 bg-light min-vh-100">
      {/* Header */}
      <div className="bg-white rounded shadow p-4 mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h1 className="h4 font-weight-bold text-dark mb-0">
            {id ? 'Edit Round Table Layout' : 'Create Round Table Layout'}
          </h1>
          <div className="d-flex gap-2">
            <button
              onClick={() => navigate('/seatmaps')}
              className="btn btn-secondary btn-sm"
            >
              Back to List
            </button>
            <button
              onClick={saveSeatMapToApi}
              className="btn btn-success btn-sm d-flex align-items-center"
            >
              <Save size={16} className="me-1" /> Save
            </button>
          </div>
        </div>

        {/* Seat Map Name */}
        <div className="d-flex align-items-center mb-3">
          {editingSeatMapName ? (
            <div className="d-flex align-items-center">
              <input
                ref={seatMapNameInputRef}
                type="text"
                value={seatMapNameEditValue}
                onChange={(e) => setSeatMapNameEditValue(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSeatMapName(seatMapNameEditValue);
                    setEditingSeatMapName(false);
                  } else if (e.key === 'Escape') {
                    setEditingSeatMapName(false);
                  }
                }}
                className="form-control me-2"
                style={{ width: '300px' }}
              />
              <button
                onClick={() => {
                  setSeatMapName(seatMapNameEditValue);
                  setEditingSeatMapName(false);
                }}
                className="btn btn-success btn-sm me-2"
              >
                <Check size={16} />
              </button>
              <button
                onClick={() => setEditingSeatMapName(false)}
                className="btn btn-danger btn-sm"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="d-flex align-items-center">
              <h5 className="mb-0 me-2">{seatMapName}</h5>
              <button
                onClick={() => {
                  setSeatMapNameEditValue(seatMapName);
                  setEditingSeatMapName(true);
                }}
                className="btn btn-outline-secondary btn-sm"
              >
                <Edit2 size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Toolbar */}
        <div className="d-flex flex-column gap-3">
          <div className="d-flex gap-2 flex-wrap align-items-center">
            <button
              onClick={() => handleAddTableClick('round')}
              className="btn btn-primary btn-sm d-flex align-items-center"
            >
              <Circle size={16} className="me-1" /> Add Round Table
            </button>
            <button
              onClick={() => handleAddTableClick('rectangular')}
              className="btn btn-info btn-sm d-flex align-items-center"
            >
              <Square size={16} className="me-1" /> Add Rectangular Table
            </button>
            <div className="vr"></div>
            <button
              onClick={() => {
                if (!hasStage) {
                  // Add stage
                  setHasStage(true);
                  setStagePosition({ x: stageSize.width / 2, y: 50 });
                  toast.success('Stage added! Click and drag to reposition. Double-click to edit name.');
                } else {
                  toast.error('Stage is already added. Click and drag to reposition. Double-click to edit name. Click the × button on the stage to delete.');
                }
              }}
              className="btn btn-warning btn-sm d-flex align-items-center"
              title={hasStage ? "Stage is added - drag to reposition" : "Add Stage"}
            >
              <Theater size={16} className="me-1" /> {hasStage ? 'Stage Added' : 'Add Stage'}
            </button>
            <div className="vr"></div>
            <div className="form-check d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                checked={snapToGrid}
                onChange={(e) => setSnapToGrid(e.target.checked)}
                id="snapToGrid"
              />
              <label className="form-check-label ms-2" htmlFor="snapToGrid">
                Snap to Grid
              </label>
            </div>
            <div className="form-check d-flex align-items-center">
              <input
                className="form-check-input"
                type="checkbox"
                checked={showGrid}
                onChange={(e) => setShowGrid(e.target.checked)}
                id="showGrid"
              />
              <label className="form-check-label ms-2" htmlFor="showGrid">
                Show Grid
              </label>
            </div>
            {showGrid && (
              <div className="d-flex align-items-center gap-2">
                <label className="form-label mb-0 small">Grid Size:</label>
                <input
                  type="number"
                  className="form-control form-control-sm"
                  value={gridSize}
                  onChange={(e) => setGridSize(parseInt(e.target.value) || 50)}
                  min="10"
                  max="200"
                  style={{ width: '70px' }}
                />
              </div>
            )}
            <div className="text-muted d-flex align-items-center ms-auto">
              Tables: {tables.length} | Total Seats: {tables.reduce((sum, t) => sum + t.seats.length, 0)}
            </div>
          </div>
        </div>
      </div>

      {/* Canvas */}
      <div className="bg-white rounded shadow p-4">
        <div className="position-relative" style={{ overflow: 'auto', maxHeight: '70vh' }}>
          <svg
            ref={stageRef}
            width={stageSize.width}
            height={stageSize.height}
            style={{ border: '1px solid #ddd', background: '#f8f9fa', userSelect: 'none' }}
            onClick={(e) => {
              if (!draggingTable && !draggingStage && e.target === e.currentTarget) {
                setSelectedTable(null);
              }
            }}
            onContextMenu={(e) => {
              e.preventDefault();
              if (e.target === e.currentTarget) {
                setSelectedTable(null);
              }
            }}
            onMouseMove={handleTableMouseMove}
            onMouseUp={handleTableMouseUp}
            onMouseLeave={handleTableMouseUp}
          >
            {/* Grid */}
            {showGrid && (
              <defs>
                <pattern id="grid" width={gridSize} height={gridSize} patternUnits="userSpaceOnUse">
                  <path d={`M ${gridSize} 0 L 0 0 0 ${gridSize}`} fill="none" stroke="#e0e0e0" strokeWidth="1"/>
                </pattern>
              </defs>
            )}
            {showGrid && <rect width="100%" height="100%" fill="url(#grid)" />}
            
            {/* Stage */}
            {hasStage && (
              <g
                transform={`translate(${stagePosition.x}, ${stagePosition.y})`}
                style={{ cursor: draggingStage ? 'grabbing' : 'grab' }}
                onMouseDown={handleStageMouseDown}
              >
                <rect
                  x={-160}
                  y={-30}
                  width={320}
                  height={60}
                  fill="#1a1a1a"
                  stroke={draggingStage ? "#FFD700" : "#000"}
                  strokeWidth={draggingStage ? 3 : 2}
                  rx={5}
                />
                {/* Delete button */}
                <circle
                  r={10}
                  fill="#dc3545"
                  cx={140}
                  cy={-40}
                  onClick={(e) => {
                    e.stopPropagation();
                    removeStage();
                  }}
                  style={{ cursor: 'pointer' }}
                />
                <text
                  x={140}
                  y={-40}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="12"
                  fill="white"
                  fontWeight="bold"
                  pointerEvents="none"
                >
                  ×
                </text>
                {editingStage ? (
                  <foreignObject x={-150} y={-20} width={300} height={40}>
                    <div className="d-flex align-items-center h-100">
                      <input
                        ref={stageInputRef}
                        type="text"
                        value={stageEditValue}
                        onChange={(e) => setStageEditValue(e.target.value)}
                        onKeyDown={handleStageKeyDown}
                        onBlur={handleStageUpdate}
                        className="form-control form-control-sm text-center"
                        style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#fff', background: 'transparent', border: '1px solid #fff' }}
                        autoFocus
                      />
                    </div>
                  </foreignObject>
                ) : (
                  <text
                    x={0}
                    y={0}
                    textAnchor="middle"
                    dominantBaseline="middle"
                    fontSize="1.25rem"
                    fontWeight="bold"
                    fill="#fff"
                    onDoubleClick={(e) => {
                      e.stopPropagation();
                      setEditingStage(true);
                      setStageEditValue(stageName);
                    }}
                    style={{ cursor: 'grab', pointerEvents: 'auto' }}
                  >
                    {stageName}
                  </text>
                )}
              </g>
            )}
            
            {/* Tables */}
            {tables.map(table => renderTable(table))}
          </svg>
        </div>
      </div>

      {/* Add Table Modal */}
      {showAddTableModal && (
        <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1000 }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Add {pendingTableType === 'round' ? 'Round' : 'Rectangular'} Table</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowAddTableModal(false)}
                />
              </div>
              <div className="modal-body">
                <div className="mb-3 d-none">
                  <label className="form-label">Maximum Seats For This Table</label>
                  <input
                    type="number"
                    className="form-control"
                    value={pendingMaxSeats}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 12;
                      setPendingMaxSeats(Math.max(1, Math.min(50, value)));
                      // Update pending seat count if it exceeds new max
                      if (pendingSeatCount > value) {
                        setPendingSeatCount(value);
                      }
                    }}
                    min="1"
                    max="50"
                  />
                  <small className="form-text text-muted">
                    Maximum number of seats allowed for this specific table (default: 10)
                  </small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Number of Seats</label>
                  <input
                    type="number"
                    className="form-control"
                    value={pendingSeatCount}
                    onChange={(e) => setPendingSeatCount(Math.min(pendingMaxSeats, Math.max(1, parseInt(e.target.value) || 1)))}
                    min="1"
                    max={pendingMaxSeats}
                    autoFocus
                  />
                  <small className="form-text text-muted">
                    Number of seats for this table (max: {pendingMaxSeats})
                  </small>
                </div>
              </div>
              <div className="modal-footer">
                <button onClick={handleAddTableConfirm} className="btn btn-primary">
                  Add Table
                </button>
                <button
                  onClick={() => setShowAddTableModal(false)}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Seat Edit Modal */}
      {editingSeat && (
        <div 
          className="modal d-block" 
          style={{ 
            backgroundColor: 'rgba(0,0,0,0.5)', 
            zIndex: 1000,
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setEditingSeat(null);
              setSeatNameInput('');
              setEditingSeatColor('');
            }
          }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h5 className="modal-title">Edit Seat</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setEditingSeat(null);
                    setSeatNameInput('');
                    setEditingSeatColor('');
                  }}
                />
              </div>
              <div className="modal-body">
                {(() => {
                  const table = tables.find(t => t.id === editingSeat?.tableId);
                  const seat = table?.seats.find(s => s.id === editingSeat?.seatId);
                  const isReserved = seat?.reserved || false;
                  
                  return (
                    <>
                      <div className="mb-3 d-none">
                        <label className="form-label">Guest Name</label>
                        <input
                          type="text"
                          className="form-control"
                          value={seatNameInput}
                          onChange={(e) => setSeatNameInput(e.target.value)}
                          placeholder="Enter guest name (optional)"
                          autoFocus
                        />
                        <small className="form-text text-muted">
                          Optional: Enter a guest name for this seat
                        </small>
                      </div>
                      <div className="mb-3">
                        <div className="form-check">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            id="reservedCheckbox"
                            checked={isReserved}
                            onChange={(e) => handleReservedChange(e.target.checked)}
                          />
                          <label className="form-check-label" htmlFor="reservedCheckbox">
                            Reserved Seat
                          </label>
                        </div>
                        <small className="form-text text-muted">
                          Mark this seat as reserved
                        </small>
                      </div>
                      <div className="mb-3">
                        <label className="form-label">Seat Color</label>
                        <div className="d-flex align-items-center gap-2">
                          <input
                            type="color"
                            className="form-control form-control-color"
                            value={editingSeatColor || '#96CEB4'}
                            onChange={(e) => setEditingSeatColor(e.target.value)}
                            style={{ width: '60px', height: '38px' }}
                          />
                          <input
                            type="text"
                            className="form-control"
                            value={editingSeatColor || ''}
                            onChange={(e) => setEditingSeatColor(e.target.value)}
                            placeholder="#96CEB4"
                          />
                          {editingSeatColor && (
                            <button
                              className="btn btn-outline-secondary btn-sm"
                              onClick={() => setEditingSeatColor('')}
                              title="Reset to default"
                            >
                              Reset
                            </button>
                          )}
                        </div>
                        <small className="form-text text-muted">
                          Custom color for this seat (leave empty for default)
                        </small>
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="modal-footer d-flex justify-content-between">
                <button onClick={handleRemoveSeat} className="btn btn-danger">
                  <Trash2 size={16} className="me-1" /> Remove Seat
                </button>
                <div className="d-flex gap-2">
                  <button
                    onClick={() => {
                      setEditingSeat(null);
                      setSeatNameInput('');
                      setEditingSeatColor('');
                    }}
                    className="btn btn-secondary"
                  >
                    Cancel
                  </button>
                  <button onClick={handleSeatNameUpdate} className="btn btn-primary">
                    Save
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Table Edit Panel */}
      {selectedTable && !editingSeat && (
        <div className="position-fixed bg-white border rounded shadow p-3" style={{ 
          right: '20px', 
          top: '50%', 
          transform: 'translateY(-50%)', 
          zIndex: 100,
          minWidth: '250px'
        }}>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="mb-0">Table Options</h6>
            <button
              className="btn-close"
              onClick={() => setSelectedTable(null)}
            />
          </div>
          {(() => {
            const table = tables.find(t => t.id === selectedTable);
            if (!table) return null;
            console.log('Selected table prices:', {
  adultPrice: table.adultPrice,
  price: table.price,
  childPrice: table.childPrice,
});

            return (
              
              <>
                <div className="mb-3">
                  <label className="form-label">Table Name</label>
                  {editingTable === table.id ? (
                    <div className="d-flex gap-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        defaultValue={table.name}
                        onBlur={(e) => {
                          updateTableName(table.id, e.target.value);
                          setEditingTable(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            updateTableName(table.id, e.target.value);
                            setEditingTable(null);
                          } else if (e.key === 'Escape') {
                            setEditingTable(null);
                          }
                        }}
                        autoFocus
                      />
                    </div>
                  ) : (
                    <div
                      className="form-control form-control-sm"
                      onClick={() => setEditingTable(table.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      {table.name} <Edit2 size={12} className="float-end" />
                    </div>
                  )}
                </div>
                <div className="mb-3">
                  <label className="form-label">Table Color</label>
                  <div className="d-flex align-items-center gap-2">
                    <input
                      type="color"
                      className="form-control form-control-color"
                      value={table.color || '#8B4513'}
                      onChange={(e) => updateTableProperty(table.id, 'color', e.target.value)}
                      style={{ width: '60px', height: '38px' }}
                    />
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      value={table.color || '#8B4513'}
                      onChange={(e) => updateTableProperty(table.id, 'color', e.target.value)}
                    />
                  </div>
                </div>
                <div className="mb-3 d-none">
                  <label className="form-label">Category</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    value={table.category || ''}
                    onChange={(e) => updateTableProperty(table.id, 'category', e.target.value)}
                    placeholder="e.g., VIP, Standard"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Adult Price</label>
                  
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    // value={table.adultPrice || 0}
                 value=   {table.adultPrice ?? table.price ?? 0}
                    onChange={(e) => updateTableProperty(table.id, 'adultPrice', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="1"
                  />
                </div>
                <div className="mb-3">
                  <label className="form-label">Child Price</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    // value={table.childPrice || 0}
                    value={table.childPrice ?? 0}
                    onChange={(e) => updateTableProperty(table.id, 'childPrice', parseFloat(e.target.value) || 0)}
                    min="0"
                    step="1"
                  />
                </div>
                <div className="mb-3 d-none">
                  <label className="form-label">Maximum Seats For This Table</label>
                  <input
                    type="number"
                    className="form-control form-control-sm"
                    value={table.maxSeatsPerTable || 12}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 12;
                      const newMax = Math.max(1, Math.min(50, value));
                      updateTableProperty(table.id, 'maxSeatsPerTable', newMax);
                      // If current seats exceed new max, show warning
                      if (table.seats.length > newMax) {
                        toast.error(`This table has ${table.seats.length} seats, but max is now ${newMax}. Remove seats to continue.`);
                      }
                    }}
                    min="1"
                    max="50"
                  />
                  <small className="form-text text-muted">
                    Maximum seats allowed for this table
                  </small>
                </div>
                <div className="mb-3">
                  <label className="form-label">Seats: {table.seats.length} / {table.maxSeatsPerTable || 12}</label>
                  <div className="d-flex gap-2">
                    <button
                      onClick={() => addSeatToTable(table.id)}
                      className="btn btn-sm btn-outline-primary"
                      disabled={table.seats.length >= (table.maxSeatsPerTable || 12)}
                    >
                      <Plus size={14} /> Add Seat
                    </button>
                  </div>
                </div>
                <div className="mb-3">
                  <label className="form-label">Reserved Seats ({table.seats.filter(s => s.reserved).length})</label>
                  <div className="d-none" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                    {table.seats.filter(s => s.reserved).map(seat => (
                      <div key={seat.id} className="d-flex justify-content-between align-items-center mb-2 p-2 bg-light rounded">
                        <span>{seat.name}</span>
                        <button
                          onClick={() => {
                            setTables(tables.map(t => 
                              t.id === table.id 
                                ? { ...t, seats: t.seats.map(s => s.id === seat.id ? { ...s, name: '', reserved: false } : s) }
                                : t
                            ));
                          }}
                          className="btn btn-sm btn-outline-danger"
                          title="Unreserve seat"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    ))}
                    {table.seats.filter(s => s.reserved).length === 0 && (
                      <div className="text-muted text-center py-2">No reserved seats</div>
                    )}
                  </div>
                </div>
              </>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default RoundTableEditor;

